import { useQueryClient } from "@tanstack/react-query";
import { Logo, useMe } from "@wirevod/app-core";
import { Button } from "@wirevod/ui";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { logout } from "@/api/auth";
import { setSuspended } from "@/api/suspension";
import styles from "./SuspendedNotice.module.css";

/**
 * Full-page notice for a workspace suspended by WireVoD staff. It polls the
 * workspace status so access returns on its own once staff restore it.
 */
export function SuspendedNotice() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = me.data?.tenant.status;
  const { refetch } = me;

  useEffect(() => {
    const timer = setInterval(() => void refetch(), 60_000);
    return () => clearInterval(timer);
  }, [refetch]);

  useEffect(() => {
    if (status === "active") setSuspended(false);
  }, [status]);

  async function signOut() {
    await logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  async function checkAgain() {
    const result = await refetch();
    if (result.data?.tenant.status === "active") setSuspended(false);
  }

  return (
    <main className={styles.page} aria-labelledby="suspended-title">
      <Logo />
      <p className={styles.code}>Account suspended</p>
      <h1 id="suspended-title" className={styles.title}>
        {me.data ? `${me.data.tenant.name} is suspended` : "This account is suspended"}
      </h1>
      <p className={styles.text}>
        WireVoD has paused this workspace, so the dashboard and the API are unavailable. Your videos
        and settings are kept.
      </p>
      <p className={styles.text}>
        To restore access, email{" "}
        <a className={styles.link} href="mailto:support@wirevod.online">
          support@wirevod.online
        </a>{" "}
        and include your workspace ID{me.data ? ` (${me.data.tenant.id})` : ""}.
      </p>
      <div className={styles.actions}>
        <Button variant="primary" loading={me.isFetching} onClick={() => void checkAgain()}>
          Check again
        </Button>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </main>
  );
}
